import React, { useMemo } from 'react';
import Stack, { contentStackSpacing } from '../../v2BaseComponents/Stack';
import { email } from '../../../utils/validators';
import { SelectOption } from '../../v2BaseComponents/Inputs/Select/types';
import { ChipSet } from '../../v2BaseComponents/Chip';
import { getIntl } from '../../../utils/intl';
import { Input, Select } from '../../v2BaseComponents/Inputs';
import Button from '../../v2BaseComponents/Button';
import Text from '../../v2BaseComponents/Text';
import { useLoadedUser } from '../../../hooks/api/useUser';
import { View } from 'react-native';
import { EmailRecipient } from './types';

const intl = getIntl('emailSelection', {
    inputEmail: 'Input email',
    selectCollaborator: 'Select collaborator',
    sendingTo: 'Sending to:',
    addEmail: 'Add Email',
    addMyEmail: 'Add my email',
});

export type EmailSelectionOwnProps = {
    recipients: EmailRecipient[];
    value: string[];
    onChange: (emails: string[]) => void;
};

export type EmailSelectionProps = EmailSelectionOwnProps;

const EmailSelection: React.FC<EmailSelectionProps> = (props) => {
    const { user } = useLoadedUser({});
    const [rawEmailInput, setRawEmailInput] = React.useState<string>('');
    const emailError = useMemo(() => email(rawEmailInput), [rawEmailInput]);
    const showError = !!rawEmailInput && rawEmailInput.length > 6;
    const selectOptions = React.useMemo<SelectOption[]>(
        () =>
            props.recipients
                .filter((_) => !props.value.includes(_.email))
                .map<SelectOption>((_) => ({
                    value: _.email,
                    label: _.name,
                })),
        [props.recipients, props.value],
    );

    const getChipLabel = (email: string) => {
        const recipient = props.recipients.find((_) => _.email === email);
        return recipient ? recipient.name : email;
    };

    return (
        <Stack vertical fullWidth space={contentStackSpacing * 2} align="center">
            {props.value.length ? (
                <Stack horizontal align="flex-start" fullWidth space={contentStackSpacing}>
                    <Text variant="bold-body-1">{intl('sendingTo')}</Text>
                    <ChipSet
                        style={{ flex: 1 }}
                        chips={props.value.map((email) => ({
                            title: getChipLabel(email),
                            value: email,
                            onDelete: () => {
                                props.onChange(props.value.filter((_) => _ !== email));
                            },
                            testID: `chip-${email}`,
                        }))}
                    />
                </Stack>
            ) : null}
            {selectOptions.length > 0 ? (
                <Select
                    title={intl('selectCollaborator')}
                    selectOptions={selectOptions}
                    value={null}
                    onChange={(value) => {
                        if (value) {
                            props.onChange([...new Set([...props.value, value])]);
                        }
                    }}
                    forceFullWidth
                    testID="email-selection-select"
                />
            ) : null}
            <Stack vertical fullWidth space={contentStackSpacing}>
                <Input
                    title={intl('inputEmail')}
                    value={rawEmailInput}
                    onChange={(e) => {
                        setRawEmailInput(e.nativeEvent.text);
                    }}
                    error={showError ? emailError : undefined}
                    forceFullWidth
                    testID="custom-email-input"
                />
                <Stack horizontal fullWidth justify="space-between" align="flex-end">
                    <View>
                        {user?.email && !props.value.includes(user.email) && (
                            <Button
                                variant="ghost"
                                title={intl('addMyEmail')}
                                icon="PlusCircle"
                                onPress={() => {
                                    props.onChange([...new Set([...props.value, user.email])]);
                                }}
                                testID="email-selection-user-email-button"
                            />
                        )}
                    </View>
                    <Button
                        title={intl('addEmail')}
                        onPress={() => {
                            props.onChange([...new Set([...props.value, rawEmailInput])]);
                            setRawEmailInput('');
                        }}
                        variant="primary"
                        icon="PlusCircle"
                        isDisabled={!!emailError || !rawEmailInput}
                        testID="email-selection-add-email-button"
                    />
                </Stack>
            </Stack>
        </Stack>
    );
};

export default React.memo(EmailSelection);
